/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faPrint } from '@fortawesome/free-solid-svg-icons/faPrint'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import { TYPE_APARTMENT, TYPE_TERRAIN } from '../../enums/EstateTypeCodes.mjs'
import { TRANSACTION_SEASONAL_RENTAL } from '../../enums/TransactionCodes.mjs'
import { isBuildingEstate, isPhysicalEstate } from '../../lib/estate.mjs'
import { randomId } from '../../lib/random.mjs'
import { patchEstate, postEstate } from '../../lib/rest/estateAPIClient'
import EstateSchema from '../../schemas/EstateSchema.mjs'
import AgencyFeesField from '../fields/AgencyFeesField'
import AvailableAtField from '../fields/AvailableAtField'
import BannerField from '../fields/BannerField'
import BathroomsField from '../fields/BathroomsField'
import BedroomsField from '../fields/BedroomsField'
import ChargesField from '../fields/ChargesField'
import CityField from '../fields/CityField'
import ColorField from '../fields/ColorField'
import ConstructionYearField from '../fields/ConstructionYearField'
import DescriptionField from '../fields/DescriptionField'
import EquippedField from '../fields/EquippedField'
import EstateReferenceField from '../fields/EstateReferenceField'
import EstateTypeField from '../fields/EstateTypeField'
import ExclusivityField from '../fields/ExclusivityField'
import FloorsField from '../fields/FloorsField'
import FormalDescriptionField from '../fields/FormalDescriptionField'
import FurnishedField from '../fields/FurnishedField'
import GateCodeField from '../fields/GateCodeField'
import GuardianField from '../fields/GuardianField'
import KitchensField from '../fields/KitchensField'
import LandAreaField from '../fields/LandAreaField'
import LivingRoomsField from '../fields/LivingRoomsField'
import NoteField from '../fields/NoteField'
import ParcelField from '../fields/ParcelField'
import ParkingField from '../fields/ParkingField'
import ParkingNumbersField from '../fields/ParkingNumbersField'
import PriceField from '../fields/PriceField'
import PublishedField from '../fields/PublishedField'
import ResidenceField from '../fields/ResidenceField'
import RoomsField from '../fields/RoomsField'
import SecurityDepositField from '../fields/SecurityDepositField'
import SurfaceField from '../fields/SurfaceField'
import TaxIncludedField from '../fields/TaxIncludedField'
import TerraceAreaField from '../fields/TerraceAreaField'
import TitleField from '../fields/TitleField'
import ToiletsField from '../fields/ToiletsField'
import TransactionField from '../fields/TransactionField'
import TrusteeField from '../fields/TrusteeField'
import FormSection from '../FormSection'
import PriceLabel from '../labels/PriceLabel'
import NoSensibleInfoHere from '../NoSensibleInfoHere'
import EstatePhotosManager from './EstatePhotosManager'
import CustomerField from '../fields/CustomerField'
import { parseBoolean } from '@jalik/form-parser'
import EstateFeaturesField from '../fields/EstateFeaturesField'
import { queryClient } from '../../../../client/js/lib/query'
import { getEstateListLink, getEstatePrintLink } from '../../lib/links/estateLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Box, Button, Checkbox, SimpleGrid, Stack, Tabs, Text } from '@mantine/core'
import { faImage } from '@fortawesome/free-solid-svg-icons/faImage'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'

export type EstateFormProps = {
  initialValues: Partial<Estate>
}

function EstateForm (props: EstateFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:estate')

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Estate>>(() => ({
      bannerColor: '#F1C2BC',
      exclusivity: false,
      features: (initialValues.EstateAndFeatures || []).map((el) => el.featureId),
      isAgencyFeesWithTax: false,
      isPricePublished: false,
      published: false,
      tmpRef: randomId(),
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(EstateSchema),
    validate: createFormValidator(EstateSchema),
    validateField: createFieldValidator(EstateSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, EstateSchema)
      return id
        ? patchEstate(id, finalValues)
        : postEstate(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['estate', id] })
      queryClient.invalidateQueries({ queryKey: ['estates'] })
    }
  })

  const { values } = form

  // todo calculate percentage depending on tax included or not
  const agencyFeesPercentage = useMemo(() => (
    values.agencyFees != null && values.price != null
      ? (values.agencyFees * 100) / values.price
      : null
  ), [values.agencyFees, values.price])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getEstateListLink() : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <Box>
          <Tabs defaultValue="main" keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="main">
                {intl.formatMessage({
                  id: 'main',
                  defaultMessage: 'Principal'
                })}
              </Tabs.Tab>
              <Tabs.Tab value="website">
                {intl.formatMessage({
                  id: 'website',
                  defaultMessage: 'Site Internet'
                })}
              </Tabs.Tab>
              <Tabs.Tab
                value="photos"
                leftSection={<FontAwesomeIcon icon={faImage} />}
              >
                {intl.formatMessage({
                  id: 'photos',
                  defaultMessage: 'Photos'
                })}
                &nbsp;
                ({values.Photos?.length || 0})</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="main" pt="md">
              <Stack>
                <SimpleGrid cols={{ lg: 2 }}>
                  <FormSection
                    defaultOpened
                    title={intl.formatMessage({
                      id: 'general',
                      defaultMessage: 'Général'
                    })}
                  >
                    <TitleField />
                    <SimpleGrid cols={{ sm: 2 }}>
                      <TransactionField />
                      <EstateTypeField disabled={id != null} />
                    </SimpleGrid>

                    <EstateReferenceField disabled />

                    <CustomerField
                      name="ownerId"
                      label={intl.formatMessage({
                        id: 'owner',
                        defaultMessage: 'Propriétaire'
                      })}
                    />
                  </FormSection>

                  <FormSection
                    defaultOpened
                    title={intl.formatMessage({
                      id: 'description',
                      defaultMessage: 'Description'
                    })}
                  >
                    <FormalDescriptionField />
                    <NoteField description={<NoSensibleInfoHere />} />
                  </FormSection>
                </SimpleGrid>

                <SimpleGrid cols={{ lg: 2 }}>
                  <FormSection
                    defaultOpened
                    title={intl.formatMessage({
                      id: 'address',
                      defaultMessage: 'Adresse'
                    })}
                  >
                    <CityField />
                    <ParcelField />
                  </FormSection>

                  {values.type === TYPE_APARTMENT ||
                  values.type === TYPE_TERRAIN
                    ? <FormSection
                      defaultOpened
                      title={intl.formatMessage({
                        id: 'residence',
                        defaultMessage: 'Résidence'
                      })}
                    >
                      <ResidenceField />
                      <TrusteeField
                        description={<FormattedMessage
                          id="trustee_help"
                          defaultMessage="Indiquer les informations de contact du syndic."
                        />}
                      />
                      <GuardianField
                        description={<FormattedMessage
                          id="guardian_help"
                          defaultMessage="Indiquer les informations de contact du gardien."
                        />}
                      />
                      <GateCodeField
                        description={<FormattedMessage
                          id="gate_code_help"
                          defaultMessage="Indiquer le code permettant d'ouvrir le portail."
                        />}
                      />
                    </FormSection>
                    : null}
                </SimpleGrid>

                <FormSection
                  defaultOpened
                  title={intl.formatMessage({
                    id: 'characteristics',
                    defaultMessage: 'Caractéristiques'
                  })}
                >
                  {isBuildingEstate(values.type)
                    ? <SimpleGrid cols={{ sm: 2, xl: 4 }}>
                      <FurnishedField nullable />
                      <EquippedField nullable />
                    </SimpleGrid>
                    : null}

                  <SimpleGrid cols={{ sm: 2, xl: 4 }}>
                    {isPhysicalEstate(values.type)
                      ? <LandAreaField />
                      : null}

                    {isBuildingEstate(values.type)
                      ? <>
                        <SurfaceField />
                        <TerraceAreaField />
                      </>
                      : null}
                  </SimpleGrid>

                  {isBuildingEstate(values.type)
                    ? <>
                      <SimpleGrid cols={{ sm: 2, xl: 4 }}>
                        <FloorsField />
                        <RoomsField />
                        <BedroomsField />
                        <LivingRoomsField />
                        <KitchensField />
                        <BathroomsField />
                        <ToiletsField />
                      </SimpleGrid>
                      <SimpleGrid cols={{ sm: 2, xl: 4 }}>
                        <ParkingField />
                        <ParkingNumbersField />
                        <ConstructionYearField />
                      </SimpleGrid>
                    </>
                    : null}

                  <EstateFeaturesField />
                </FormSection>

                <SimpleGrid cols={{ lg: 2 }}>
                  <FormSection
                    defaultOpened
                    title={intl.formatMessage({
                      id: 'rent_and_guaranties',
                      defaultMessage: 'Loyer et garantie'
                    })}
                  >
                    <PriceField
                      name="price"
                      label={PriceLabel({ value: values.transaction })}
                    />
                    <FormField
                      name="isPricePublished"
                      label={intl.formatMessage({
                        id: 'show_price_on_website',
                        defaultMessage: 'Afficher le prix sur le site internet'
                      })}
                      parser={parseBoolean}
                      render={Checkbox}
                      type="checkbox"
                      value="true"
                    />
                    <SecurityDepositField
                      label={intl.formatMessage({
                        id: 'security_deposit',
                        defaultMessage: 'Dépôt de garantie'
                      })}
                    />
                  </FormSection>

                  <FormSection
                    defaultOpened
                    title={intl.formatMessage({
                      id: 'angecy_fees',
                      defaultMessage: 'Frais d\'agence'
                    })}
                  >
                    <AgencyFeesField
                      amount={values.agencyFees}
                      disabled={values.transaction === TRANSACTION_SEASONAL_RENTAL}
                      total={values.price}
                      label={intl.formatMessage({
                        id: 'agency_fees',
                        defaultMessage: 'Honoraires de location ({percentage}%)'
                      }, {
                        percentage: agencyFeesPercentage != null
                          ? intl.formatNumber(
                            agencyFeesPercentage,
                            { maximumFractionDigits: 4 }
                          )
                          : '?'
                      })}
                    />
                    <TaxIncludedField
                      disabled={values.transaction === TRANSACTION_SEASONAL_RENTAL}
                      name="isAgencyFeesWithTax"
                    />
                  </FormSection>
                </SimpleGrid>

                <FormSection
                  defaultOpened
                  title={intl.formatMessage({
                    id: 'Charges',
                    defaultMessage: 'Charges'
                  })}
                >
                  <ChargesField name="Charges" />
                </FormSection>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="website" pt="md">
              <Stack>
                <SimpleGrid cols={{ md: 2 }}>
                  <BannerField />
                  <ColorField
                    name="bannerColor"
                    label={intl.formatMessage({
                      id: 'banner_color',
                      defaultMessage: 'Couleur de fond de la bannière'
                    })}
                  />
                </SimpleGrid>
                <Text>
                  <FormattedMessage
                    id="banner_help"
                    defaultMessage="Texte du bandeau affiché sur les annonces (ex: 'EXCEPTIONNEL'), laisser vide pour ne pas afficher de bandeau."
                  />
                </Text>
                <Box
                  p="md"
                  style={{
                    backgroundColor: form.values.bannerColor,
                    opacity: 0.9
                  }}
                >
                  <b>
                    {form.values.banner || form.values.bannerColor}
                  </b>
                </Box>
                <DescriptionField description={<NoSensibleInfoHere />} />
                <SimpleGrid cols={{ lg: 2 }}>
                  <AvailableAtField />
                </SimpleGrid>
                <ExclusivityField
                  description={<FormattedMessage
                    id="estate_exclusivity_help"
                    defaultMessage="Cocher pour afficher cette annonce dans les exclusivités sur le site internet."
                  />}
                />
                <PublishedField
                  description={<FormattedMessage
                    id="estate_published_help"
                    defaultMessage="Cocher pour publier cette annonce sur le site internet."
                  />}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="photos" pt="md">
              <EstatePhotosManager
                estateId={id}
                tmpRef={values.tmpRef}
              />
            </Tabs.Panel>
          </Tabs>
        </Box>

        <ButtonGroup>
          {id != null
            ? <Button
              disabled={form.disabled}
              component={AnchorLink}
              target="_blank"
              to={getEstatePrintLink(values.id)}
              leftSection={
                <FontAwesomeIcon
                  fixedWidth
                  icon={faPrint}
                />}
            >
              <FormattedMessage
                id="print"
                defaultMessage="Imprimer"
              />
            </Button>
            : null}

          {editable
            ? <>
              <FormResetButton />
              <FormSaveButton />
            </>
            : null}
        </ButtonGroup>
      </Stack>
    </SmartForm>
  )
}

export default EstateForm
